import { get, merge, has } from 'lodash';
import { BaseClient as InfraBaseClient, sessionManager } from 'vue-infra';
import BaseClient from '../common/BaseClient';
import ApiResources from '../data/apiResources';
import { WORKATO_TIMEOUT_ERROR_CODE,
  SOURCE_APP_NAME,
  WORKATO_DOMAINS,
  IHUB_SHELL_DOMAINS,
  WORKATO_PLATFORM_DOMAIN,
  IHUB_CONFIG, IHUB_SHELL_PLATFORM_DOMAIN, WORKATO_CONFIG_NAME } from '../common/consts';

const RESOURCES = {
  WORKATO_TOKEN: 'workatoToken',
  WORKATO_RECIPE: 'workatoRecipe',
  WORKATO_ADMINS: 'workatoAdmins',
  EXISTING_WORKATO_ACCOUNT: 'existingWorkatoAccount',
  EXISTING_WORKATO_USER: 'existingWorkatoUser',
  IMPORT_WORKATO_RECIPE: 'importWorkatoRecipe',
  NEW_WORKATO_USER: 'newWorkatoUser',
  NEW_WORKATO_ACCOUNT: 'newWorkatoAccount',
  IHUB_CONFIG: 'ihubConfig',
  WORKATO_SETTINGS: 'workatoSettings',
  CREATE_WORKATO_ONBOARDING_POPUP_SETTING: 'workatoCreateOnboardingPopup',
  UPDATE_WORKATO_ONBOARDING_POPUP_SETTING: 'workatoUpdateOnboardingPopup',
};

const RESOURCES_CONFIGURATION = {
  [RESOURCES.WORKATO_TOKEN]: {
    ALLOWED_METHODS: [BaseClient.HTTP_METHODS.GET],
    URL_KEY: () => 'WORKATO.WorkatoToken',
  },
  [RESOURCES.WORKATO_RECIPE]: {
    ALLOWED_METHODS: [BaseClient.HTTP_METHODS.GET],
    URL_KEY: () => 'WORKATO.WorkatoRecipe',
  },
  [RESOURCES.WORKATO_ADMINS]: {
    ALLOWED_METHODS: [BaseClient.HTTP_METHODS.GET],
    URL_KEY: () => 'WORKATO.WorkatoAdmins',
  },
  [RESOURCES.EXISTING_WORKATO_ACCOUNT]: {
    ALLOWED_METHODS: [BaseClient.HTTP_METHODS.GET],
    URL_KEY: () => 'WORKATO.ExistingWorkatoAccount',
  },
  [RESOURCES.EXISTING_WORKATO_USER]: {
    ALLOWED_METHODS: [BaseClient.HTTP_METHODS.GET],
    URL_KEY: () => 'WORKATO.ExistingWorkatoUser',
  },
  [RESOURCES.IMPORT_WORKATO_RECIPE]: {
    ALLOWED_METHODS: [BaseClient.HTTP_METHODS.PUT],
    URL_KEY: () => 'WORKATO.ImportWorkatoRecipe',
  },
  [RESOURCES.NEW_WORKATO_USER]: {
    ALLOWED_METHODS: [BaseClient.HTTP_METHODS.PUT],
    URL_KEY: () => 'WORKATO.NewWorkatoUser',
  },
  [RESOURCES.NEW_WORKATO_ACCOUNT]: {
    ALLOWED_METHODS: [BaseClient.HTTP_METHODS.PUT],
    URL_KEY: () => 'WORKATO.NewWorkatoAccount',
  },
  [RESOURCES.IHUB_CONFIG]: {
    ALLOWED_METHODS: [BaseClient.HTTP_METHODS.GET],
    URL_KEY: () => 'WORKATO.IhubConfig',
  },
  [RESOURCES.WORKATO_SETTINGS]: {
    ALLOWED_METHODS: [BaseClient.HTTP_METHODS.GET],
    URL_KEY: () => 'WORKATO.WorkatoSettings',
  },
  [RESOURCES.CREATE_WORKATO_ONBOARDING_POPUP_SETTING]: {
    ALLOWED_METHODS: [BaseClient.HTTP_METHODS.POST],
    URL_KEY: () => 'WORKATO.CreateWorkatoOnboardingPopupSetting',
  },
  [RESOURCES.UPDATE_WORKATO_ONBOARDING_POPUP_SETTING]: {
    ALLOWED_METHODS: [BaseClient.HTTP_METHODS.PUT],
    URL_KEY: () => 'WORKATO.UpdateWorkatoOnboardingPopupSetting',
  },
};

class WorkatoClient extends BaseClient {
  constructor({
    resource,
    contextName,
    ...options
  }) {
    const infraBaseClient = createWorkatoBaseClient(options, resource, contextName);
    super({
      name: 'WorkatoClient',
      client: infraBaseClient,
      ...options,
    });
  }
}

function createWorkatoBaseClient(options, resource, contextName) {
  // TODO: need to implement out own urlResolver
  const urlResolver = window.LE.urlResolver;
  setIhubDomains(urlResolver, options);
  const objFromApiResources = get(ApiResources, RESOURCES_CONFIGURATION[resource].URL_KEY());
  const baseURL = urlResolver.getUrl(objFromApiResources);
  const params = objFromApiResources ? objFromApiResources.params : {};
  const workatoBaseClientOptions = {
    allowedMethods: RESOURCES_CONFIGURATION[resource].ALLOWED_METHODS,
    source: {
      appName: SOURCE_APP_NAME,
      contextName,
    },
    config: {
      baseURL,
      timeout: RESOURCES_CONFIGURATION[resource].TIMEOUT,
      params,
    },
    retryConfig: {
      retries: 1,
      retryCondition: error => get(error, 'response.status') !== WORKATO_TIMEOUT_ERROR_CODE,
      retryDelay: () => 1000,
      shouldResetTimeout: true,
    },
  };
  merge(workatoBaseClientOptions, options);
  return new InfraBaseClient(workatoBaseClientOptions);
}

// Todo: Remove when workatoPlatform and ihubshellPlatform domains will be added to CSDS
function setIhubDomains(urlResolver, options) {
  let accountZone = sessionManager.getAccountZone();
  if (accountZone === 'qa' || !has(WORKATO_DOMAINS, accountZone)) {
    accountZone = 'z0';
  }
  const workatoPlatformDomain = WORKATO_DOMAINS[accountZone].workatoPlatform;
  const ihubConfig = options.ihubConfig;
  const configName = options.configName;
  urlResolver.addConfigValue(WORKATO_PLATFORM_DOMAIN, workatoPlatformDomain);
  urlResolver.addConfigValue(IHUB_CONFIG, ihubConfig);
  urlResolver.addConfigValue(WORKATO_CONFIG_NAME, configName);
  const ihubshellPlatformDomain = IHUB_SHELL_DOMAINS[accountZone].ihubshellPlatform;
  urlResolver.addConfigValue(IHUB_SHELL_PLATFORM_DOMAIN, ihubshellPlatformDomain);
}

WorkatoClient.RESOURCES = RESOURCES;
WorkatoClient.RESOURCES_CONFIGURATION = RESOURCES_CONFIGURATION;

export default WorkatoClient;
